/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @interface DataItemAPI
 * @classdesc API class that is used to work with data items.
 * @example slots.getDataItem(<dataItemId>)
 * @example slots.getDataItemList()[<index>]
 * @example slots.getMappingInfoList()[<index>].dataItem
 */
define([], () => {
	class DataItemAPI {

		/**
		 * @description Returns the unique ID that identifies the given data item.
		 *
		 * @function DataItemAPI#getId
		 * @public
		 *
		 * @return {String} ID of the data item
		 */
		getId() { }

		// setLabel is not exposed as API. It is not needed for now.
		// /**
		//  * @description Set dataitem label.
		//  *
		//  * @function DataItemAPI#setLabel
		//  * @private
		//  *
		//  * @param {String} label
		//  */
		// setLabel() { }

		/**
		 * @description Returns the data item label.
		 *
		 * @function DataItemAPI#getLabel
		 * @public
		 *
		 * @return {String} Label of the data item
		 */
		getLabel() { }

		/**
		 * @description Returns the item ID that this data item references. An item ID is the metadata columnId.
		 *
		 * @function DataItemAPI#getColumnId
		 * @public
		 *
		 * @return {String} columnId
		 */
		getColumnId() { }

		/**
		 * @description Returns the navigation path ID associated with this data item.
		 *
		 * @function DataItemAPI#getNavigationPathId
		 * @private
		 *
		 * @return {String} navigationPathId
		 */
		getNavigationPathId() { }

		/**
		 * @description Sets the navigation path ID associated with this data item.
		 *
		 * @function DataItemAPI#setNavigationPathId
		 * @private
		 *
		 * @param {String} id
		 * @param {transactionToken} [transactionToken]
		 *
		 */
		setNavigationPathId() { }

		/**
		 * @description Returns the item data type.
		 * @function DataItemAPI#getDataType
		 * @public
		 *
		 * @return {String} dataType { 'string' | 'decimal' }
		 */
		getDataType() { }

		/**
		 * @description Returns true if the data item is a data or time type. Data or time types include: date, datetime, time, year.
		 * @function DataItemAPI#isDateOrTimeType
		 * @private
		 *
		 * @return {Boolean} isDateOrTimeType
		 */
		isDateOrTimeType() { }

		/**
		 * @description Returns the type of the data item. The type can be either 'attribute' or 'fact'.
		 * @function DataItemAPI#getType
		 * @public
		 *
		 * @return {String} type
		 */
		getType() { }

		/**
		 * @description Returns the data item aggregation type.
		 * @function DataItemAPI#getAggregation
		 * @public
		 *
		 * @return {String} aggregationType is explicitly set, or has a value, otherwise the default metadata aggregation value
		 *
		 */
		getAggregation() { }


		/**
		 * @description Sets the data item aggregation type.
		 * @function DataItemAPI#setAggregation
		 * @public
		 *
		 * @param {aggregation} [aggregation = null]
		 * @param {transactionToken} [transactionToken]
		 */
		setAggregation(/*aggregation, transactionToken*/) { }

		/**
		 * @description Returns true if the data item aggregation type was not explicitly set.
		 * @function DataItemAPI#hasDefaultAggregation
		 * @public
		 *
		 * @return {Boolean} hasDefaultAggregation
		 *
		 */
		hasDefaultAggregation() { }


		/**
		 * @description Returns true if the data item sort type was not explicitly set.
		 * @function DataItemAPI#hasDefaultSort
		 * @public
		 *
		 * @return {Boolean} hasDefaultSort
		 *
		 */
		hasDefaultSort() { }

		/**
		 * @description Returns true if the data item format type was not explicitly set.
		 * @function DataItemAPI#hasDefaultFormat
		 * @public
		 *
		 * @return {Boolean} hasDefaultFormat
		 *
		 */
		hasDefaultFormat() { }

		/**
		 * @description Returns the column metadata that is associated with this data item.
		 *
		 * @function DataItemAPI#getMetadataColumn
		 * @private
		 *
		 * @return {MetadataColumnAPI} columnMetadata returns null if the metadata item is not available
		 */
		getMetadataColumn() { }


		/**
		 * @description Returns the data format for this data item.
		 * @function DataItemAPI#getFormat
		 * @public
		 *
		 * @param {Object} options ignoreDefaultFormatting (ignore any default formatting)
		 * @return {FormatSpec} Format spec
		 */
		getFormat(/* options */) {}


		/**
		 * @description Sets the format spec for the given data item.
		 * @function DataItemAPI#setFormat
		 * @public
		 *
		 * @param {FormatSpec} formatSpec
		 * @param {transactionToken} transactionToken
		 *
		 */
		setFormat(/*formatSpec, transactionToken*/) { }

		/**
		 * @description Gets the effective sort spec. If a user sort is set, then it is returned. Otherwise, the default sort is returned.
		 *
		 * @function DataItemAPI#getSort
		 * @public
		 * @return {SortSpec}
		 *
		 * @example
		 *  {
		 *    type: String ('asc' | 'desc' | 'null'),
		 *    priority: Number,
		 *    by: String  ('caption' | 'value'),
		 *    context: String (column id used for the sort context)
		 *    custom: Object (specify sort overrides)
		 *    custom.top: String[] (Use values of members to move to the top of the sorted list)
		 *    custom.enabled: Boolean (indicate if custom sort is enabled)
		 *  }
		 *
		 */
		getSort() { }

		/**
		 * @description Gets the top/bottom information that is set on the data item.
		 * @function DataItemAPI#getTopBottom
		 * @public
		 * @example
		 *  {
		 *    type: String ('topcount' | 'toppercent' | 'bottomcount' | 'bottompercent'),
		 *    value: Number,
		 *    rank: boolean
		 *    context: Object (with itemId and aggregate)
		 *  }
		 *
		 * @return {TopBottomInfo}
		 *
		 */
		getTopBottom() { }

		/**
		 * @description Sets the top/bottom information on the data item.
		 * @function DataItemAPI#setTopBottom
		 * @public
		 *
		 * @example
		 *  {
		 *    type: String ('topcount' | 'toppercent' | 'bottomcount' | 'bottompercent'),
		 *    value: Number,
		 *    rank: boolean,
		 *    context:  measure (column id) can be used when the topbottom applied on the categories
		 *  }
		 *
		 * @param {TopBottomInfo} topBottomInfo
		 * @param {transactionToken} transactionToken
		 *
		 */
		setTopBottom(/* topBottomInfo, transactionToken */) {
		}

		/**
		 * @description Returns true if the data item is a hierarchy and we can drill down.
		 * @function DataItemAPI#canDrillDown
		 * @public
		 *
		 * @return {Boolean} canDrillDown
		 */
		canDrillDown() { }


		/**
		 * @description Drill down in a data item.
		 *
		 * @function DataItemAPI#drillDown
		 * @public
		 *
		 * @param {String} value
		 * @param {transactionToken} transactionToken
		 *
		 *
		 */
		drillDown(/* value, transactionToken */) { }

		/**
		 * @description Gets the drill down value (member id) if a drill down is set.
		 *
		 * @function DataItemAPI#getDrillDownValue
		 * @public
		 *
		 * @return {String} drillDown value
		 */
		getDrillDownValue() {}

		/**
		 * @description Gets the isolated value (leaf member id) if drill down is in isolated state.
		 * 
		 * @function DataItemAPI#getIsolatedValue
		 * @private
		 * 
		 * @return {String} isolated member id
		 */
		getIsolatedValue() {}

		/**
		 * @description Drill up in a data item.
		 *
		 * @function DataItemAPI#drillUp
		 * @public
		 *
		 * @param {String} value
		 * @param {transactionToken} transactionToken
		 *
		 *
		 */
		drillUp(/* value, transactionToken */) { }

		/**
		 * @description Gets the drill up value (member id) if a drill up is set.
		 *
		 * @function DataItemAPI#getDrillDownValue
		 * @public
		 *
		 * @return {String} drillUp value
		 */
		getDrillUpValue() {}


		/**
		 * @description Clears the drill up or down selection.
		 *
		 * @function DataItemAPI#clearDrill
		 * @public
		 *
		 * @param {transactionToken} transactionToken
		 */
		clearDrill() {}

		/**
		 * @description Enables binning in the data item.
		 * @function DataItemAPI#setBinning
		 * @private
		 *
		 * @param {BinningSpec} binningSpec - null to clear the binning
		 * @param {transactionToken} transactionToken
		 *
		 */
		setBinning(/* binningSpec, transactionToken */) { }


		/**
		 * @description Gets the binning json object if specified for this data item.
		 * @function DataItemAPI#getBinning
		 * @private
		 *
		 * @return {BinningSpec} Returns binning spec. If there is no binning, returns undefined.
		 */
		getBinning() { }


		/**
		 * @description Returns true if the model sort type has been set.
		 * @function DataItemAPI#hasModelSort
		 * @private
		 *
		 * @return {Boolean} hasModelSort
		 */
		hasModelSort() { }

		/**
		 * @description Sets the type, sort-by method and priority of sort on the data item.
		 *
		 * @function DataItemAPI#setSort
		 * @public
		 * @param {SortSpec} sortSpec
		 * @param {transactionToken} transactionToken
		 *
		 */
		setSort(/* sortSpec, transactionToken */) { }

		/**
		 * @description Gets the union information that is set on the data item.
		 * @function DataItemAPI#getUnion
		 * @private
		 * @return {UnionAPI}
		 */
		getUnion() { }

		/**
		 * @description Indicates if the data item is not available. Returns true if the column was deleted or hidden.
		 * @function DataItemAPI#isUnavailable
		 * @public
		 * @return {Boolean}
		 */
		isColumnUnavailable() { }
	}

	/**
	 * @description Enum for all supported topbottom type.
	 * @readonly
	 * @enum {String}
	 * @constant
	 * @memberOf DataItemAPI
	 */
	DataItemAPI.TOPBOTTOM_TYPE = {
		topcount : 'topcount',
		toppercent : 'toppercent',
		bottomcount : 'bottomcount',
		bottompercent : 'bottompercent'
	};

	/**
	 * @description Enum for all supported aggregation type.
	 * @readonly
	 * @enum {String}
	 * @constant
	 * @memberOf DataItemAPI
	 */
	DataItemAPI.AGGREGATION_TYPE = {
		none: 'none',
		average: 'avg',
		automatic: 'automatic',
		calculated: 'calculated',
		count: 'count',
		countDistinct: 'countdistinct',
		maximum: 'max',
		median: 'median',
		minimum: 'min',
		standardDeviation: 'stddev',
		total: 'sum',
		variance: 'variance'
	};

	return DataItemAPI;
});
